"use client";
import React, { useMemo } from "react";
import { useParams } from "next/navigation";
import {
  SlidersHorizontal,
  ChevronDown,
  LayoutGrid,
  Grid3X3,
} from "lucide-react";
import styles from "./topFilterBar.module.css";
import productColors from "@/mockDatas/productColors.json";
import { useProduct } from "@/context/ProductContext";

interface TopFilterBarProps {
  totalCount?: number;
  visibleCount?: number;
  selectedDmnvts: string[];
  onDmnvtChange: (ids: string[]) => void;
  allDmnvts?: any[];
  allowedNganhHang?: string[];
  groupDefinitions?: { ma_nganh_hang: string; name: string }[];
  sortQuery: string;
  onSortChange: (sort: string) => void;
  gridCols: 3 | 4;
  onGridColsChange: (cols: 3 | 4) => void;
  onOpenMobileFilter?: () => void;
  onResetAll?: () => void;
  isSingleSector?: boolean;
  locale?: string;
}

export default function TopFilterBar({
  selectedDmnvts,
  onDmnvtChange,
  allDmnvts = [],
  allowedNganhHang = ["TRA", "CAFE", "NS", "TPCB"],
  groupDefinitions,
  sortQuery,
  onSortChange,
  gridCols,
  onGridColsChange,
  onOpenMobileFilter,
  isSingleSector,
  locale,
}: TopFilterBarProps) {
  const { getTabColor } = useProduct();
  const params = useParams();
  const currentLocale = locale || (params?.locale as string) || "vi";
  const isEn = currentLocale === "en";

  const sortOptions = useMemo(
    () => [
      { value: "newest", label: isEn ? "Newest" : "Mới nhất" },
      { value: "price-asc", label: isEn ? "Price: Low → High" : "Giá: Thấp → Cao" },
      { value: "price-desc", label: isEn ? "Price: High → Low" : "Giá: Cao → Thấp" },
      { value: "name-asc", label: isEn ? "Name: A → Z" : "Tên: A → Z" },
    ],
    [isEn]
  );

  // Xác định xem có phải chế độ ngành hàng đơn (trang con) hay đa ngành hàng (trang mẹ)
  const isSingle =
    isSingleSector ??
    (Array.isArray(allowedNganhHang) && allowedNganhHang.length === 1);

  // Available quick pills:
  // - Nếu là 1 ngành hàng: hiển thị các nhóm con (dmnvt) của ngành hàng đó (ví dụ: các nhóm cà phê)
  // - Nếu là đa ngành hàng: hiển thị các ngành hàng (Trà, Cà phê, Nông sản,...)
  const quickPills = useMemo(() => {
    if (isSingle) {
      const targetSector = (allowedNganhHang[0] || "").toUpperCase().trim();
      const sectorItems = (allDmnvts || []).filter((item: any) => {
        if (!item?.ma_nganh_hang) return false;
        return (
          String(item.ma_nganh_hang).toUpperCase().trim() === targetSector
        );
      });

      return sectorItems.map((item: any) => ({
        id: item._id,
        label: item.ten_nvt || item.ten_nganh_hang || item.ma_nvt,
      }));
    }

    const definitions = (groupDefinitions ||
      productColors.group_definitions ||
      []) as {
      ma_nganh_hang: string;
      name: string;
    }[];

    return definitions
      .filter((item) => allowedNganhHang.includes(item.ma_nganh_hang))
      .map((item) => ({
        id: item.ma_nganh_hang,
        label: item.name,
      }));
  }, [isSingle, allowedNganhHang, allDmnvts, groupDefinitions]);

  // Handle quick pill click
  const handleQuickPillClick = (key: string | "all") => {
    if (key === "all") {
      onDmnvtChange([]);
      return;
    }

    if (isSingle) {
      // Chế độ 1 ngành hàng: key là _id của nhóm sản phẩm
      const isCurrentlyActive =
        selectedDmnvts.length === 1 && selectedDmnvts[0] === key;
      if (isCurrentlyActive) {
        // Bấm lại nhóm đang chọn -> bỏ chọn (về Tất cả)
        onDmnvtChange([]);
      } else {
        onDmnvtChange([key]);
      }
      return;
    }

    // Chế độ đa ngành hàng: key là ma_nganh_hang
    const groupItems = allDmnvts.filter(
      (item: any) =>
        item?.ma_nganh_hang &&
        String(item.ma_nganh_hang).toUpperCase() === key.toUpperCase()
    );
    const groupIds = groupItems.map((item: any) => item._id);

    if (groupIds.length === 0) return;

    // Check if this pill is currently the only active selection
    const isCurrentlyActive =
      groupIds.every((id) => selectedDmnvts.includes(id)) &&
      selectedDmnvts.length === groupIds.length;

    if (isCurrentlyActive) {
      // Toggle off -> show all
      onDmnvtChange([]);
    } else {
      // Set to this category
      onDmnvtChange(groupIds);
    }
  };

  // Determine active quick pill
  const activeQuickPill = useMemo(() => {
    if (selectedDmnvts.length === 0) return "all";

    if (isSingle) {
      if (selectedDmnvts.length === 1) {
        return selectedDmnvts[0];
      }
      return null;
    }

    for (const pill of quickPills) {
      const groupItems = allDmnvts.filter(
        (item: any) =>
          item?.ma_nganh_hang &&
          String(item.ma_nganh_hang).toUpperCase() ===
            pill.id.toUpperCase()
      );
      const groupIds = groupItems.map((item: any) => item._id);
      if (
        groupIds.length > 0 &&
        groupIds.every((id) => selectedDmnvts.includes(id)) &&
        selectedDmnvts.length === groupIds.length
      ) {
        return pill.id;
      }
    }
    return null;
  }, [selectedDmnvts, isSingle, quickPills, allDmnvts]);

  // Lấy màu sắc cho tab Tất cả
  const allTabColor = useMemo(() => {
    return getTabColor(
      {
        id: "all",
        label: isEn ? "All" : "Tất cả",
        ma_nganh_hang: isSingle ? allowedNganhHang?.[0] : undefined,
      },
      isSingle ? allowedNganhHang?.[0] : undefined
    );
  }, [getTabColor, isSingle, allowedNganhHang, isEn]);

  // Lấy màu sắc cho từng pill
  const getPillColor = (pill: { id: string; label: string }) => {
    return getTabColor(
      {
        id: pill.id,
        label: pill.label,
        ma_nganh_hang: isSingle ? allowedNganhHang?.[0] : pill.id,
      },
      isSingle ? allowedNganhHang?.[0] : undefined
    );
  };

  return (
    <div className={styles.topBarCard}>
      {/* ═══ ROW 1: TOP ACTION ROW (Hint & Mobile Button on Left, Sort & View on Right) ═══ */}
      <div className={styles.topControlRow}>
        <div className={styles.topLeftGroup}>
          {onOpenMobileFilter && (
            <button
              type="button"
              className={styles.mobileFilterBtn}
              onClick={onOpenMobileFilter}
            >
              <SlidersHorizontal size={14} />
              <span>{isEn ? "Filters" : "Bộ Lọc"}</span>
              <span className={styles.mobileFilterBadge}>
                {selectedDmnvts.length}
              </span>
            </button>
          )}
          <span className={styles.hintText}>
            {isEn
              ? "*Quickly select product line to view details"
              : "*Chọn nhanh tên dòng sản phẩm để xem chi tiết"}
          </span>
        </div>

        {/* Right: Sort & Grid View Switchers */}
        <div className={styles.rightSection}>
          <div className={styles.sortGroup}>
            <label htmlFor="topSortSelect" className={styles.sortLabel}>
              {isEn ? "Sort by:" : "Sắp xếp theo:"}
            </label>
            <div className={styles.sortSelectWrap}>
              <select
                id="topSortSelect"
                className={styles.sortSelect}
                value={sortQuery}
                onChange={(e) => onSortChange(e.target.value)}
              >
                {sortOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <ChevronDown size={14} className={styles.sortChevron} />
            </div>
          </div>

          {/* Grid column switchers: 4 cols vs 3 cols */}
          <div className={styles.gridSwitchGroup}>
            <button
              type="button"
              className={`${styles.gridSwitchBtn} ${gridCols === 4 ? styles.gridSwitchActive : ""}`}
              onClick={() => onGridColsChange(4)}
              title={isEn ? "4 Columns" : "4 Cột"}
              aria-label={isEn ? "4 Columns" : "4 Cột"}
            >
              <LayoutGrid size={16} />
            </button>
            <button
              type="button"
              className={`${styles.gridSwitchBtn} ${gridCols === 3 ? styles.gridSwitchActive : ""}`}
              onClick={() => onGridColsChange(3)}
              title={isEn ? "3 Columns" : "3 Cột"}
              aria-label={isEn ? "3 Columns" : "3 Cột"}
            >
              <Grid3X3 size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* ═══ ROW 2: TABS ROW (Card Buttons) ═══ */}
      <div className={styles.quickPillsList}>
        <button
          type="button"
          className={`${styles.quickPill} ${activeQuickPill === "all" ? styles.activeQuickPill : ""}`}
          style={
            {
              "--pill-color": allTabColor,
              ...(activeQuickPill === "all"
                ? {
                    backgroundColor: allTabColor,
                    color: "#ffffff",
                    borderColor: allTabColor,
                    boxShadow: `0 4px 12px ${allTabColor}40`,
                  }
                : {}),
            } as React.CSSProperties
          }
          onClick={() => handleQuickPillClick("all")}
        >
          {isEn ? "All" : "Tất cả"}
        </button>
        {quickPills.map((pill) => {
          const pillColor = getPillColor(pill);
          const isActive = activeQuickPill === pill.id;
          return (
            <button
              key={pill.id}
              type="button"
              className={`${styles.quickPill} ${isActive ? styles.activeQuickPill : ""}`}
              style={
                {
                  "--pill-color": pillColor,
                  ...(isActive
                    ? {
                        backgroundColor: pillColor,
                        color: "#ffffff",
                        borderColor: pillColor,
                        boxShadow: `0 4px 12px ${pillColor}40`,
                      }
                    : {}),
                } as React.CSSProperties
              }
              onClick={() => handleQuickPillClick(pill.id)}
            >
              {pill.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
